import { isAxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useDialog } from '@zep/dialog';

import {
  useRemovePaymentCard,
  useSubscriptionPlan,
  useUpdateZipCode,
} from '../../../../services';
import { pushGoogleAdsPurchaseConversion } from '../../../../utils/gtmMarketingEvent';
import { useGoSignin } from '../../subscription';
import {
  usePlanPaymentFormContext,
  usePlanPaymentInfoContext,
} from '../PlanPaymentSection/Providers';

import { usePlanStatusType } from './usePlanStatusType';

export const usePlanSubscriptionAction = () => {
  const { t } = useTranslation(['subscription']);
  const router = useRouter();
  const {
    planPaymentInfo,
    spaceHashId,
    isUpdate,
    selectedPlanInfo,
    setIsPlanPaymentLoading,
  } = usePlanPaymentInfoContext();
  const { watch, getValues } = usePlanPaymentFormContext();
  const { mutateAsync: _mutateSubscriptionPlan } = useSubscriptionPlan();
  const { mutateAsync: removePaymentCard } = useRemovePaymentCard();
  const { mutateAsync: mutateZipCode } = useUpdateZipCode();
  const planType = watch('planType');
  const planStatusType = usePlanStatusType({
    planType,
  });
  const dialog = useDialog();
  const moveSignIn = useGoSignin();

  const handleSubscribePlan = async (_payload: {
    retry: () => void;
    isRemoveBillingKey: boolean;
    /** 결제 버튼에 표시된 실청구액. Google Ads 구매 전환에 사용된다. */
    chargedAmount?: { value: number; currency: string };
  }) => {
    const { isRemoveBillingKey, chargedAmount } = _payload;

    setIsPlanPaymentLoading(true);
    try {
      const spaceCountryCode = planPaymentInfo?.space.countryCode;
      if (!spaceCountryCode) {
        throw new Error('Missing space country code');
      }

      const { data } = await _mutateSubscriptionPlan({
        spacePlanTemplateId: selectedPlanInfo.id,
        isUpdated: isUpdate,
        spaceHashId,
        countryCode: spaceCountryCode,
      });
      if (spaceCountryCode === 'US') {
        await mutateZipCode({
          spaceHashId,
          zipCode: getValues('zipCode') || '',
        });
      }

      // Google Ads 구매 전환 이벤트 스니펫을 실제 결제 정보로 전송한다.
      // 사용자가 조작 가능한 URL query가 아니라, 서버가 결제를 확인한 응답
      // (payment.orderId)과 결제 버튼의 실청구액(신규=VAT 포함 finalPrice,
      // 업그레이드=추가결제금액)을 사용한다. 즉시 결제가 없는 플로우
      // (다운그레이드 등)는 payment 부재/0원 가드로 제외된다.
      if (data?.payment?.orderId && chargedAmount) {
        pushGoogleAdsPurchaseConversion({
          value: chargedAmount.value,
          currency: chargedAmount.currency,
          transactionId: data.payment.orderId,
        });
      }

      moveCompletePage(data?.spacePlan?.spacePlanHashId);
    } catch (e: any) {
      setIsPlanPaymentLoading(false);
      const title = t('subscription:payment.alertFailPay.title');
      const { type, message } = findSubscriptionError(e);

      switch (type) {
        case '401': {
          await dialog.alert({
            title,

            // @ts-ignore
            content: t[message],
            useOutSideClick: false,
          });
          moveSignIn(true);
          break;
        }
        case 'PG_ERROR': {
          await dialog.alert({
            title,
            content: t('subscription:SpaceDetail.changeCardModal.reason', {
              message,
            }),
            useOutSideClick: false,
          });
          break;
        }
        case 'FAIL_TO_EDIT_ZIP_CODE': {
          await dialog.alert({
            title: t('subscription:errorModal.zipCode.title'),
            content: t('subscription:errorModal.zipCode.content'),
          });
          break;
        }
        case 'ZEP_API_ERROR': {
          const confirmed = await dialog.confirm({
            title: t('subscription:payment.alertFailPay.title'),
            content: t(
              'subscription:payment.alertFailPay.descriptionErrorCode',
              { errorCode: message },
            ),
            confirmText: t('subscription:payment.alertFailPay.buttonContact'),
          });
          if (confirmed) {
            window.open(t('subscription:pricing.contact.buttonLink'), '_blank');
          }
          break;
        }
        case 'UNKNOWN_ERROR': {
          await dialog.alert({
            title,
            content: 'UNKNOWN_ERROR',
            useOutSideClick: false,
          });
        }
      }

      console.error(e);
      /** 신규등록일 경우에만 카드정보를 삭제후 새롭게 요청한다 */
      if (!isUpdate && isRemoveBillingKey) {
        removePaymentCard(spaceHashId);
      }

      throw new Error('Error Occurred during subscription plan');
    }
  };

  const moveCompletePage = (spacePlanHashId?: string) => {
    router.push(
      {
        pathname: `/plan-payment/${spacePlanHashId}/success`,
        query: {
          ...router.query,
          updateStatus: planStatusType,
        },
      },
      `/plan-payment/${spacePlanHashId}/success`,
    );
  };

  return {
    handleSubscribePlan,
  };
};

const FAIL_TO_BILLING_PAYMENT_BY_PG = -158;
const FAIL_TO_EDIT_BILLING = -157;
const FAIL_TO_CREATE_BILLING = -155;
const FAIL_TO_EDIT_ZIP_CODE = -211;
const findSubscriptionError = (
  e: any,
): {
  type:
    | '401'
    | 'PG_ERROR'
    | 'ZEP_API_ERROR'
    | 'UNKNOWN_ERROR'
    | 'FAIL_TO_EDIT_ZIP_CODE';
  message: string;
} => {
  if (e.code === 'ECONNABORTED') {
    return { type: 'ZEP_API_ERROR', message: e.message };
  }

  if (isAxiosError(e) && e.response?.status === 401) {
    return {
      type: '401',
      message: 'subscription:payment.alertFailPay.descriptionLogOut',
    };
  }

  // 서버에서 내려주는 에러코드가 담긴 키가 다양하게 존재하여 해당 키를 찾아줌
  const findErrorStatusKey = (_e: any) => {
    if (_e.code) {
      return _e.code;
    }
    if (_e.statusKey) {
      return _e.statusKey;
    }
    return _e.status;
  };

  // ZEP API ERROR
  if (e.status < 0) {
    const errorCode = +e.status;
    if (
      errorCode === FAIL_TO_BILLING_PAYMENT_BY_PG ||
      errorCode === FAIL_TO_CREATE_BILLING ||
      errorCode === FAIL_TO_EDIT_BILLING
    ) {
      return {
        type: 'PG_ERROR',
        message: e?.message ?? `${findErrorStatusKey(e)}`,
      };
    }

    if (errorCode === FAIL_TO_EDIT_ZIP_CODE) {
      return {
        type: 'FAIL_TO_EDIT_ZIP_CODE',
        message: e?.statusKey,
      };
    }

    return {
      type: 'ZEP_API_ERROR',
      message: `${findErrorStatusKey(e)}`,
    };
  }
  return {
    type: 'UNKNOWN_ERROR',
    message: 'An error occurred',
  };
};
